import { useState, useEffect } from "react";
import { identifierSchemas } from "@pineseed/spec";
import type { z } from "zod";
import { EntityTable, type Column } from "../components/EntityTable";
import { Modal } from "../components/Modal";
import { useAdmin } from "../context/AdminContext";
import { useCopyToClipboard } from "@pineseed/gui/hook/useCopyToClipboard";
import { CopyableText } from "@pineseed/gui/component/CopyableText";

type ParticipantUserMapping = z.infer<typeof identifierSchemas.ParticipantUserMapping>;
type LinkSource = z.infer<typeof identifierSchemas.LinkSource>;

type View = 'mappings' | 'lookup' | 'stats';

export function IdentifierPanel() {
  const { identifier } = useAdmin();
  const [view, setView] = useState<View>('mappings');
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [showUnlinkAllConfirm, setShowUnlinkAllConfirm] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  
  // Lookup state
  const [lookupType, setLookupType] = useState<'participant' | 'user'>('participant');
  const [lookupInput, setLookupInput] = useState('');
  const [lookupResult, setLookupResult] = useState<any>(null);
  
  // Stats state
  const [stats, setStats] = useState<{
    totalMappings: number;
    byLinkSource: Record<LinkSource, number>;
    recentMappings: ParticipantUserMapping[];
  } | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [limit] = useState(50);

  // Copy feedback
  const { copyToClipboard, isCopied } = useCopyToClipboard();

  // Load initial mappings
  useEffect(() => {
    if (view === 'mappings') {
      identifier.getAllMappings(limit, page * limit);
    }
  }, [view, page, limit]); // Don't include identifier - it's from context

  // Load stats when switching to stats view
  useEffect(() => {
    if (view === 'stats') {
      // First load mappings, then compute stats
      identifier.getAllMappings(100, 0).then(() => {
        const statsData = identifier.getStats();
        setStats(statsData);
      });
    }
  }, [view]); // Only depend on view change

  const handleLookup = async () => {
    if (!lookupInput.trim()) return;

    try {
      if (lookupType === 'participant') {
        const mapping = await identifier.getMappingByParticipant(lookupInput.trim());
        if (mapping) {
          setLookupResult({
            type: 'mapping',
            data: mapping,
          });
        } else {
          setLookupResult({
            type: 'not_found',
            message: 'No mapping found for this participant',
          });
        }
      } else {
        const result = await identifier.getParticipantsByUser(lookupInput.trim());
        if (result.participantIds.length > 0) {
          const mappings = await identifier.getMappingsByUser(lookupInput.trim());
          setLookupResult({
            type: 'user_mappings',
            data: mappings,
            count: result.total,
          });
        } else {
          setLookupResult({
            type: 'not_found',
            message: 'No participants found for this user',
          });
        }
      }
    } catch (err) {
      setLookupResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Lookup failed',
      });
    }
  };

  const handleUnlink = async (participantId: string) => {
    if (!confirm(`Unlink participant ${participantId}?`)) return;
    
    await identifier.unlinkParticipant(participantId);
    
    // Refresh current view
    if (view === 'mappings') {
      identifier.getAllMappings(limit, page * limit);
    } else if (lookupResult?.type === 'user_mappings') {
      // Refresh lookup results
      handleLookup();
    }
  };

  const handleUnlinkAll = async () => {
    if (!selectedUserId) return;
    
    const count = await identifier.unlinkAllUserParticipants(selectedUserId);
    setShowUnlinkAllConfirm(false);
    setSelectedUserId(null);
    
    alert(`Unlinked ${count} participant(s)`);
    
    // Refresh current view
    if (view === 'mappings') {
      identifier.getAllMappings(limit, page * limit);
    }
  };

  const columns: Column<ParticipantUserMapping>[] = [
    {
      key: 'ids',
      label: 'Participant / User ID',
      render: (mapping) => (
        <div className="flex flex-col gap-1 min-w-0">
          <CopyableText
            id={mapping.participantId}
            onCopy={copyToClipboard}
            isPulsing={isCopied(mapping.participantId)}
            className="truncate"
          >
            P: {mapping.participantId.slice(0, 12)}...
          </CopyableText>
          <CopyableText
            id={mapping.userId}
            onCopy={copyToClipboard}
            isPulsing={isCopied(mapping.userId)}
            variant="muted"
            className="truncate"
          >
            U: {mapping.userId.slice(0, 8)}...{mapping.userId.slice(-4)}
          </CopyableText>
        </div>
      ),
    },
    {
      key: 'linkSource',
      label: 'Source',
      render: (mapping) => (
        <span className={`px-2 py-0.5 rounded text-xs font-medium w-fit ${
          mapping.linkSource === 'login' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
          mapping.linkSource === 'signup' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
          mapping.linkSource === 'manual' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
          'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
        }`}>
          {mapping.linkSource}
        </span>
      ),
    },
    {
      key: 'device',
      label: 'Device Info',
      render: (mapping) => mapping.deviceInfo ? (
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs text-gray-900 dark:text-gray-100 truncate">
            {mapping.deviceInfo.platform}
          </span>
          {mapping.deviceInfo.fingerprint && (
            <span className="text-xs font-mono text-gray-500 dark:text-gray-400 truncate">
              {mapping.deviceInfo.fingerprint.slice(0, 8)}...
            </span>
          )}
          {mapping.deviceInfo.userAgent && (
            <span className="text-xs text-gray-500 dark:text-gray-400 truncate" title={mapping.deviceInfo.userAgent}>
              {mapping.deviceInfo.userAgent.slice(0, 30)}...
            </span>
          )}
        </div>
      ) : (
        <span className="text-xs text-gray-400">—</span>
      ),
    },
    {
      key: 'linkedAt',
      label: 'Linked',
      render: (mapping) => (
        <span className="text-xs text-gray-600 dark:text-gray-400">
          {new Date(mapping.linkedAt).toLocaleString()}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Identifier
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Manage participant-to-user mappings
          </p>
        </div>
      </div>

      {/* View Selector */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setView('mappings')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            view === 'mappings'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          All Mappings
        </button>
        <button
          onClick={() => setView('lookup')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            view === 'lookup'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Lookup
        </button>
        <button
          onClick={() => setView('stats')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            view === 'stats'
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
          }`}
        >
          Statistics
        </button>
      </div>

      {/* Mappings View */}
      {view === 'mappings' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Showing {identifier.mappings.length} mappings
            </p>
            <button
              onClick={() => setShowLinkForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Link New Mapping
            </button>
          </div>

          <EntityTable
            data={identifier.mappings}
            columns={columns}
            onDelete={(mapping) => handleUnlink(mapping.participantId)}
            loading={identifier.loading}
            emptyMessage="No mappings found"
          />

          {/* Pagination */}
          <div className="flex justify-between items-center">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0 || identifier.loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Page {page + 1}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={identifier.mappings.length < limit || identifier.loading}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Lookup View */}
      {view === 'lookup' && (
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Lookup Mapping
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Lookup Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={lookupType === 'participant'}
                      onChange={() => setLookupType('participant')}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">By Participant ID</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={lookupType === 'user'}
                      onChange={() => setLookupType('user')}
                      className="text-blue-600"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">By User ID</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {lookupType === 'participant' ? 'Participant ID' : 'User ID (UUID)'}
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={lookupInput}
                    onChange={(e) => setLookupInput(e.target.value)}
                    placeholder={lookupType === 'participant' ? 'Enter participant ID...' : 'Enter user UUID...'}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                  />
                  <button
                    onClick={handleLookup}
                    disabled={!lookupInput.trim() || identifier.loading}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Lookup
                  </button>
                </div>
              </div>
            </div>

            {/* Lookup Results */}
            {lookupResult && (
              <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                {lookupResult.type === 'mapping' && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100">Mapping Found</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Participant ID:</span>
                        <span className="ml-2 font-mono text-gray-900 dark:text-gray-100">
                          {lookupResult.data.participantId}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">User ID:</span>
                        <span className="ml-2 font-mono text-gray-900 dark:text-gray-100">
                          {lookupResult.data.userId}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Link Source:</span>
                        <span className="ml-2 text-gray-900 dark:text-gray-100">
                          {lookupResult.data.linkSource}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Linked At:</span>
                        <span className="ml-2 text-gray-900 dark:text-gray-100">
                          {new Date(lookupResult.data.linkedAt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleUnlink(lookupResult.data.participantId)}
                      className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                    >
                      Unlink
                    </button>
                  </div>
                )}

                {lookupResult.type === 'user_mappings' && (
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                        Found {lookupResult.count} Mapping(s)
                      </h4>
                      <button
                        onClick={() => {
                          setSelectedUserId(lookupInput.trim());
                          setShowUnlinkAllConfirm(true);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
                      >
                        Unlink All
                      </button>
                    </div>
                    <EntityTable
                      data={lookupResult.data}
                      columns={columns}
                      onDelete={(mapping) => handleUnlink(mapping.participantId)}
                      loading={false}
                      emptyMessage="No mappings"
                    />
                  </div>
                )}

                {lookupResult.type === 'not_found' && (
                  <p className="text-gray-600 dark:text-gray-400">{lookupResult.message}</p>
                )}

                {lookupResult.type === 'error' && (
                  <p className="text-red-600 dark:text-red-400">Error: {lookupResult.message}</p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats View */}
      {view === 'stats' && stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Mappings</h3>
              <p className="text-3xl font-bold text-gray-900 dark:text-gray-100 mt-2">
                {stats.totalMappings}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Login</h3>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">
                {stats.byLinkSource.login}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Signup</h3>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400 mt-2">
                {stats.byLinkSource.signup}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Manual</h3>
              <p className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">
                {stats.byLinkSource.manual}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Migration</h3>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">
                {stats.byLinkSource.migration}
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              Recent Mappings
            </h3>
            <EntityTable
              data={stats.recentMappings}
              columns={columns}
              onDelete={(mapping) => handleUnlink(mapping.participantId)}
              loading={false}
              emptyMessage="No recent mappings"
            />
          </div>
        </div>
      )}

      {/* Link Form Modal */}
      <Modal
        isOpen={showLinkForm}
        onClose={() => setShowLinkForm(false)}
        title="Link Participant to User"
        maxWidth="lg"
      >
        <LinkMappingForm
          onSubmit={async (data) => {
            await identifier.linkParticipantToUser(data);
            setShowLinkForm(false);
            identifier.getAllMappings(limit, page * limit);
          }}
          onCancel={() => setShowLinkForm(false)}
        />
      </Modal>

      {/* Unlink All Confirmation Modal */}
      <Modal
        isOpen={showUnlinkAllConfirm}
        onClose={() => {
          setShowUnlinkAllConfirm(false);
          setSelectedUserId(null);
        }}
        title="Confirm Unlink All"
        maxWidth="md"
      >
        <div className="p-6 space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Are you sure you want to unlink ALL participants for user:
          </p>
          <p className="font-mono text-sm bg-gray-100 dark:bg-gray-800 p-2 rounded">
            {selectedUserId}
          </p>
          <p className="text-sm text-red-600 dark:text-red-400">
            This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2 pt-4">
            <button
              onClick={() => {
                setShowUnlinkAllConfirm(false);
                setSelectedUserId(null);
              }}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleUnlinkAll}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Unlink All
            </button>
          </div>
        </div>
      </Modal>

      {/* Error Display */}
      {identifier.error && (
        <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{identifier.error}</p>
        </div>
      )}
    </div>
  );
}

// Link Mapping Form Component
function LinkMappingForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (data: {
    participantId: string;
    userId: string;
    linkSource: LinkSource;
    deviceInfo?: {
      userAgent: string;
      platform: string;
      fingerprint?: string;
    };
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [participantId, setParticipantId] = useState('');
  const [userId, setUserId] = useState('');
  const [linkSource, setLinkSource] = useState<LinkSource>('manual');
  const [includeDeviceInfo, setIncludeDeviceInfo] = useState(false);
  const [userAgent, setUserAgent] = useState('');
  const [platform, setPlatform] = useState('');
  const [fingerprint, setFingerprint] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const data: any = {
        participantId: participantId.trim(),
        userId: userId.trim(),
        linkSource,
      };

      if (includeDeviceInfo && userAgent && platform) {
        data.deviceInfo = {
          userAgent: userAgent.trim(),
          platform: platform.trim(),
          ...(fingerprint ? { fingerprint: fingerprint.trim() } : {}),
        };
      }

      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link mapping');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Participant ID <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={participantId}
          onChange={(e) => setParticipantId(e.target.value)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          User ID (UUID) <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          required
          pattern="[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Link Source <span className="text-red-500">*</span>
        </label>
        <select
          value={linkSource}
          onChange={(e) => setLinkSource(e.target.value as LinkSource)}
          required
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="login">Login</option>
          <option value="signup">Signup</option>
          <option value="manual">Manual</option>
          <option value="migration">Migration</option>
        </select>
      </div>

      <div>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeDeviceInfo}
            onChange={(e) => setIncludeDeviceInfo(e.target.checked)}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Include Device Info</span>
        </label>
      </div>

      {includeDeviceInfo && (
        <div className="space-y-3 pl-6 border-l-2 border-gray-200 dark:border-gray-700">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              User Agent <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={userAgent}
              onChange={(e) => setUserAgent(e.target.value)}
              required={includeDeviceInfo}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Platform <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              required={includeDeviceInfo}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fingerprint (optional)
            </label>
            <input
              type="text"
              value={fingerprint}
              onChange={(e) => setFingerprint(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Linking...' : 'Link Mapping'}
        </button>
      </div>
    </form>
  );
}

